import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import ImageDropZone from "./ImageDropZone";
import { Slider } from "@/components/ui/slider";
import { compressToWebP, withUploadTimeout } from "@/lib/imageCompression";

interface FooterLink { label: string; href: string; }
interface FooterColumn { title: string; links: FooterLink[]; }
interface SocialLink { name: string; href: string; icon: string; }
interface FooterSettings {
  id: number;
  description: string | null;
  copyright: string | null;
  columns: FooterColumn[];
  socials: SocialLink[];
}

const AdminFooter = () => {
  const [data, setData] = useState<FooterSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [footerLogo, setFooterLogo] = useState<string>("");
  const [footerLogoHeight, setFooterLogoHeight] = useState<number>(40);
  const [savingLogoSize, setSavingLogoSize] = useState(false);

  useEffect(() => {
    supabase
      .from("footer_settings")
      .select("*")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setData(data as unknown as FooterSettings);
      });
    supabase
      .from("site_images")
      .select("url")
      .eq("slot", "footer_logo")
      .maybeSingle()
      .then(({ data }) => {
        if (data?.url) setFooterLogo(data.url);
      });
    supabase
      .from("site_content")
      .select("value")
      .eq("key", "ui.footer_logo_height_px")
      .maybeSingle()
      .then(({ data }) => {
        const n = Number(data?.value);
        if (Number.isFinite(n) && n > 0) setFooterLogoHeight(n);
      });
  }, []);

  const uploadFooterLogo = async (file: File) => {
    const isVector = /svg|icon|ico/i.test(file.type) || /\.(svg|ico)$/i.test(file.name);
    const uploadFile = isVector ? file : await compressToWebP(file, { maxSizeMB: 0.5 });
    const ext = isVector ? (file.name.split(".").pop() || "png").toLowerCase() : "webp";
    const path = `footer_logo.${ext}`;

    await supabase.storage
      .from("site-images")
      .remove([
        "footer_logo.png",
        "footer_logo.jpg",
        "footer_logo.jpeg",
        "footer_logo.webp",
        "footer_logo.svg",
        "footer_logo.ico",
      ])
      .catch(() => {});

    const { error: uploadError } = await withUploadTimeout(
      supabase.storage
        .from("site-images")
        .upload(path, uploadFile, { upsert: true, contentType: uploadFile.type })
    );
    if (uploadError) throw new Error("Erreur upload : " + uploadError.message);

    const { data: urlData } = supabase.storage.from("site-images").getPublicUrl(path);
    const url = urlData.publicUrl + "?t=" + Date.now();

    const { error: dbError } = await supabase
      .from("site_images")
      .upsert({ slot: "footer_logo", url }, { onConflict: "slot" });
    if (dbError) throw new Error("Erreur DB : " + dbError.message);

    setFooterLogo(url);
    toast.success("Logo du pied de page mis à jour !");
  };

  const resetFooterLogo = async () => {
    const { error } = await supabase.from("site_images").delete().eq("slot", "footer_logo");
    if (error) return toast.error(error.message);
    setFooterLogo("");
    toast.success("Logo du pied de page réinitialisé (utilise le logo global)");
  };

  const saveFooterLogoHeight = async () => {
    setSavingLogoSize(true);
    const { error } = await supabase
      .from("site_content")
      .upsert(
        { key: "ui.footer_logo_height_px", value: String(footerLogoHeight) },
        { onConflict: "key,lang" }
      );
    setSavingLogoSize(false);
    if (error) toast.error(error.message);
    else toast.success(`Hauteur enregistrée (${footerLogoHeight}px)`);
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    const { error } = await supabase
      .from("footer_settings")
      .upsert({
        id: 1,
        description: data.description,
        copyright: data.copyright,
        columns: data.columns as any,
        socials: data.socials as any,
      });
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success("Pied de page sauvegardé");
  };

  if (!data) return <div className="text-muted-foreground text-sm p-4">Chargement…</div>;

  const addColumn = () => setData({ ...data, columns: [...data.columns, { title: "Nouvelle colonne", links: [] }] });
  const removeColumn = (i: number) => setData({ ...data, columns: data.columns.filter((_, j) => j !== i) });
  const addLink = (i: number) => {
    const cols = [...data.columns];
    cols[i].links.push({ label: "Nouveau lien", href: "#" });
    setData({ ...data, columns: cols });
  };
  const removeLink = (i: number, j: number) => {
    const cols = [...data.columns];
    cols[i].links = cols[i].links.filter((_, k) => k !== j);
    setData({ ...data, columns: cols });
  };
  const addSocial = () => setData({ ...data, socials: [...data.socials, { name: "Nouveau", href: "#", icon: "globe" }] });
  const removeSocial = (i: number) => setData({ ...data, socials: data.socials.filter((_, j) => j !== i) });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg border border-border bg-card/50 p-4">
        <div>
          <h3 className="font-heading text-lg font-semibold">Gestionnaire du pied de page</h3>
          <p className="font-body text-sm text-muted-foreground mt-1">
            Description, copyright, colonnes de liens, réseaux sociaux — appliqué à toutes les pages.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Sauvegarde…" : "Sauvegarder"}
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Texte général</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Description courte</Label>
            <Textarea rows={3} value={data.description ?? ""} onChange={(e) => setData({ ...data, description: e.target.value })} />
          </div>
          <div>
            <Label>Copyright</Label>
            <Input value={data.copyright ?? ""} onChange={(e) => setData({ ...data, copyright: e.target.value })} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Logo du pied de page</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ImageDropZone
            currentUrl={footerLogo}
            onUpload={uploadFooterLogo}
            accept="image/png,image/svg+xml,image/webp"
            label="Glissez-déposez ou cliquez pour uploader"
            height="h-40"
          />
          <p className="font-body text-xs text-muted-foreground">
            PNG transparent recommandé. Affiché en blanc dans le pied de page. S'il est vide, le logo global du site est utilisé.
          </p>

          <div className="flex items-center gap-4 pt-2 border-t border-border">
            <Label className="whitespace-nowrap">Hauteur</Label>
            <div className="flex-1">
              <Slider
                value={[footerLogoHeight]}
                min={20}
                max={120}
                step={2}
                onValueChange={(v) => setFooterLogoHeight(v[0] ?? 40)}
              />
            </div>
            <div className="w-16 text-right font-mono text-sm">{footerLogoHeight} px</div>
            <Button size="sm" onClick={saveFooterLogoHeight} disabled={savingLogoSize}>
              <Save className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </div>

          {footerLogo && (
            <div className="flex items-center justify-between rounded-md border border-border bg-primary px-3 py-2">
              <div className="flex items-center gap-3">
                <span className="text-xs text-primary-foreground/70">Aperçu :</span>
                <img
                  src={footerLogo}
                  alt="Logo footer"
                  style={{ height: `${footerLogoHeight}px` }}
                  className="w-auto object-contain brightness-0 invert"
                />
              </div>
              <Button size="sm" variant="ghost" onClick={resetFooterLogo} className="text-primary-foreground hover:bg-primary-foreground/10">
                <Trash2 className="w-4 h-4 mr-1" /> Réinitialiser
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Colonnes de liens</CardTitle>
          <Button size="sm" onClick={addColumn}><Plus className="w-4 h-4 mr-1" /> Colonne</Button>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          {data.columns.map((col, i) => (
            <div key={i} className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Input value={col.title} onChange={(e) => {
                  const c = [...data.columns]; c[i].title = e.target.value; setData({ ...data, columns: c });
                }} className="font-semibold" />
                <Button size="icon" variant="ghost" onClick={() => removeColumn(i)} aria-label="Supprimer">
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
              {col.links.map((l, j) => (
                <div key={j} className="flex items-center gap-2">
                  <Input value={l.label} placeholder="Label" onChange={(e) => {
                    const c = [...data.columns]; c[i].links[j].label = e.target.value; setData({ ...data, columns: c });
                  }} />
                  <Input value={l.href} placeholder="https://…" onChange={(e) => {
                    const c = [...data.columns]; c[i].links[j].href = e.target.value; setData({ ...data, columns: c });
                  }} />
                  <Button size="icon" variant="ghost" onClick={() => removeLink(i, j)} aria-label="Supprimer">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => addLink(i)}>
                <Plus className="w-4 h-4 mr-1" /> Lien
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Réseaux sociaux</CardTitle>
          <Button size="sm" onClick={addSocial}><Plus className="w-4 h-4 mr-1" /> Réseau</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.socials.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={s.name} placeholder="Nom" onChange={(e) => {
                const arr = [...data.socials]; arr[i].name = e.target.value; setData({ ...data, socials: arr });
              }} />
              <Input value={s.href} placeholder="URL" onChange={(e) => {
                const arr = [...data.socials]; arr[i].href = e.target.value; setData({ ...data, socials: arr });
              }} />
              <Input value={s.icon} placeholder="icon (whatsapp, instagram…)" onChange={(e) => {
                const arr = [...data.socials]; arr[i].icon = e.target.value; setData({ ...data, socials: arr });
              }} />
              <Button size="icon" variant="ghost" onClick={() => removeSocial(i)} aria-label="Supprimer">
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminFooter;